import {
  keepPreviousData,
  type QueryFunctionContext,
  skipToken,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { api } from '@/api';
import { END_POINT } from '@/constants/end-point.constants';
import { ORGANIZATION_QUERY_KEYS } from '@/constants/query-keys.constants';

import type {
  OrganizationOfMeResponse,
  OrganizationResponse,
  OrganizationsResponse,
} from '../types';

const fetchOrganizationDetail = (
  id: string,
): Promise<AxiosResponse<OrganizationResponse>> =>
  api.get(END_POINT.organizationDetail(id));

export const useOrganizationDetail = (id: string) =>
  useQuery({
    queryKey: ORGANIZATION_QUERY_KEYS.detail(id),
    placeholderData: keepPreviousData,
    queryFn: id ? () => fetchOrganizationDetail(id) : skipToken,
    select: data => data.data,
  });

const fetchOrganizations = ({
  queryKey: [{ filters }],
}: QueryFunctionContext<
  ReturnType<(typeof ORGANIZATION_QUERY_KEYS)['list']>
>): Promise<AxiosResponse<OrganizationsResponse>> =>
  api.get(END_POINT.organizations, { params: filters });

export const useOrganizations = (filters?: unknown) =>
  useQuery({
    queryKey: ORGANIZATION_QUERY_KEYS.list(filters),
    queryFn: fetchOrganizations,
    placeholderData: keepPreviousData,
    select: data => data.data,
  });

const fetchOrganizationsOfMe = ({
  queryKey: [{ filters }],
}: QueryFunctionContext<
  ReturnType<(typeof ORGANIZATION_QUERY_KEYS)['organizationsOfMe']>
>): Promise<AxiosResponse<OrganizationOfMeResponse>> =>
  api.get(END_POINT.organizationsOfMe, { params: filters });

export const useFetchOrganizationsOfMe = (includeNoShops = true) =>
  useQuery({
    queryKey: ORGANIZATION_QUERY_KEYS.organizationsOfMe(includeNoShops),
    queryFn: fetchOrganizationsOfMe,
    select: data => data.data,
  });

export const useOrganizationsOfMe = (includeNoShops = true) => {
  const queryClient = useQueryClient();
  const queryKey = ORGANIZATION_QUERY_KEYS.organizationsOfMe(includeNoShops);
  const axiosResponse =
    queryClient.getQueryData<AxiosResponse<OrganizationOfMeResponse>>(queryKey);

  return (axiosResponse?.data ?? {}) as OrganizationOfMeResponse;
};
